import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  DataList,
  type DataListItem,
  LoadingAnimation,
  Modal,
  Table,
  type TableColumn,
} from "@knowtex-ai/components";
import { Download } from "iconoir-react";
import type { McArtifact, McFindingDetail, McFindingIssue } from "../../lib/mc";
import { useMcIssueQuery } from "../../hooks/useMcRuns";
import ArtifactDownloadButton from "./ArtifactDownloadButton";
import ClampedText from "./ClampedText";
import {
  formatCount,
  issueRef,
  ISSUE_STATUS_BADGES,
  severityVariant,
} from "./format";
import { IssueTriageForm, issueUrl } from "./McIssuesPage";
import NewTabLink from "./NewTabLink";
import TimeCell from "./TimeCell";
import { useRetained } from "./useRetained";
import { evidenceKeys } from "./runData";

const SeverityBadge = ({ severity }: { severity?: string }) =>
  severity ? (
    <Badge size="small" variant={severityVariant(severity)}>
      {severity}
    </Badge>
  ) : null;

const LongText = ({ text }: { text?: string }) =>
  text ? <p className="whitespace-pre-wrap break-words">{text}</p> : null;

function FindingIssueCell({
  issue,
  live,
}: {
  issue?: McFindingIssue;
  live: boolean;
}) {
  if (!issue)
    return (
      <span className="text-[13px] text-gray-500">
        {live ? "After the run" : "—"}
      </span>
    );
  return (
    <NewTabLink
      to={issueUrl(issue.id)}
      aria-label={`Open issue ${issueRef(issue.id)}`}
    >
      <span className="font-mono">{issueRef(issue.id)}</span>
    </NewTabLink>
  );
}

/** The finding's issue, with its status and note editable in place. */
function FindingIssueTriage({ issue }: { issue: McFindingIssue }) {
  const query = useMcIssueQuery(issue.id);
  const full = query.data?.issue;
  return full ? (
    <IssueTriageForm key={`${full.id}:${full.updatedAt}`} issue={full} />
  ) : query.isLoading ? (
    <div className="flex justify-center py-4 text-gray-400">
      <LoadingAnimation size={20} />
    </div>
  ) : null;
}

const DETAIL_FIELDS: [keyof McFindingDetail, string][] = [
  ["description", "Description"],
  ["repro", "Steps to reproduce"],
  ["expected", "Expected"],
  ["actual", "Actual"],
];

function FindingModal({
  runId,
  finding: selected,
  artifacts,
  onClose,
}: {
  runId: string;
  finding: McFindingDetail | null;
  artifacts: McArtifact[];
  onClose: () => void;
}) {
  const finding = useRetained(selected);
  const items = (f: McFindingDetail): DataListItem[] => {
    const evidence = evidenceKeys(f, artifacts);
    const rows: (DataListItem | false)[] = [
      {
        id: "id",
        label: "ID",
        value: <span className="font-mono">{f.id}</span>,
      },
      !!f.issue && {
        id: "issue",
        label: f.issue.firstSeenHere ? "First reported as" : "Already known as",
        value: (
          <NewTabLink
            to={issueUrl(f.issue.id)}
            aria-label={`Open issue ${issueRef(f.issue.id)}`}
          >
            <span className="font-mono">{issueRef(f.issue.id)}</span>
          </NewTabLink>
        ),
      },
      {
        id: "title",
        label: "Summary",
        value: f.title ? <LongText text={f.title} /> : "—",
      },
      {
        id: "severity",
        label: "Severity",
        value: f.severity ? <SeverityBadge severity={f.severity} /> : "—",
      },
      {
        id: "kind",
        label: "Kind",
        value: f.kind ? <span className="font-mono">{f.kind}</span> : "—",
      },
      { id: "mission", label: "Mission", value: f.missionId || "—" },
      {
        id: "recorded",
        label: "Recorded",
        value: <TimeCell iso={f.recordedAt} />,
      },
      ...DETAIL_FIELDS.map(
        ([field, label]): DataListItem | false =>
          typeof f[field] === "string" &&
          !!f[field] && {
            id: field,
            label,
            value: <LongText text={f[field] as string} />,
          },
      ),
      evidence.length > 0 && {
        id: "evidence",
        label: "Evidence",
        value: (
          <ul className="flex flex-col gap-1">
            {evidence.map((key) => (
              <li key={key} className="flex items-center justify-between gap-2">
                <span className="min-w-0 font-mono break-all text-[13px]">
                  {key}
                </span>
                {artifacts.some((a) => a.key === key) && (
                  <ArtifactDownloadButton runId={runId} artifactKey={key} />
                )}
              </li>
            ))}
          </ul>
        ),
      },
      !!f.artifactKey && {
        id: "record",
        label: "Finding record",
        value: (
          <span className="flex items-center justify-between gap-2">
            <span className="min-w-0 font-mono break-all text-[13px]">
              {f.artifactKey}
            </span>
            <ArtifactDownloadButton runId={runId} artifactKey={f.artifactKey} />
          </span>
        ),
      },
    ];
    return rows.filter((row): row is DataListItem => Boolean(row));
  };

  return (
    <Modal
      open={!!selected}
      onOpenChange={(open) => !open && onClose()}
      title={finding ? `Finding ${finding.id}` : "Finding"}
      size="large"
    >
      {finding && (
        <div className="flex flex-col gap-5">
          {finding.issue && <FindingIssueTriage issue={finding.issue} />}
          <DataList
            items={items(finding)}
            size="small"
            aria-label={`Finding ${finding.id}`}
          />
        </div>
      )}
    </Modal>
  );
}

interface RunFindingsProps {
  runId: string;
  findings: McFindingDetail[];
  artifacts: McArtifact[];
  /** Findings are linked to issues when the run finishes. */
  live: boolean;
  onExport: () => void;
}

export default function RunFindings({
  runId,
  findings,
  artifacts,
  live,
  onExport,
}: RunFindingsProps) {
  const [params, setParams] = useSearchParams();
  const openId = params.get("finding");
  const open = findings.find((f) => f.id === openId) ?? null;
  const setOpenId = useCallback(
    (id: string | null) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set("finding", id);
          else next.delete("finding");
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const section = useRef<HTMLDivElement>(null);
  const linked = useRef(openId);
  const linkedLoaded = !!linked.current && !!open;
  useEffect(() => {
    if (!linkedLoaded) return;
    section.current?.scrollIntoView?.({ block: "start" });
    linked.current = null;
  }, [linkedLoaded]);
  const columns = useMemo(
    (): TableColumn<McFindingDetail>[] => [
      {
        id: "id",
        header: "ID",
        width: 90,
        accessor: "id",
        cell: (f) => <span className="font-mono">{f.id}</span>,
      },
      {
        id: "severity",
        header: "Severity",
        width: 110,
        accessor: "severity",
        cell: (f) => <SeverityBadge severity={f.severity} />,
      },
      {
        id: "kind",
        header: "Kind",
        width: 140,
        accessor: "kind",
        cell: (f) =>
          f.kind ? <span className="font-mono">{f.kind}</span> : "—",
      },
      {
        id: "title",
        header: "Finding",
        width: 440,
        grow: true,
        accessor: "title",
        cell: (f) => <ClampedText text={f.title} lines={3} />,
      },
      {
        id: "issue",
        header: "Issue",
        width: 190,
        accessor: (f) =>
          f.issue
            ? (ISSUE_STATUS_BADGES[f.issue.status]?.label ?? f.issue.status)
            : "",
        cell: (f) => <FindingIssueCell issue={f.issue} live={live} />,
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        width: 88,
        sticky: "end",
        hideable: false,
        cell: (f) => (
          <Button
            variant="secondary"
            size="small"
            onClick={() => setOpenId(f.id)}
            aria-label={`View finding ${f.id}`}
          >
            View
          </Button>
        ),
      },
    ],
    [live, setOpenId],
  );

  return (
    <div ref={section} className="scroll-mt-6">
      <Card
        variant="elevated"
        title="Findings"
        subtitle={`${formatCount(findings.length)} reported`}
        actions={
          findings.length > 0 && (
            <Button
              variant="secondary"
              size="small"
              icon={Download}
              onClick={onExport}
            >
              Export CSV
            </Button>
          )
        }
      >
        <Table
          aria-label="Findings"
          size="small"
          columns={columns}
          data={findings}
          getRowId={(f) => f.id}
          paginated={false}
          emptyState="No findings reported."
        />
        <FindingModal
          runId={runId}
          finding={open}
          artifacts={artifacts}
          onClose={() => setOpenId(null)}
        />
      </Card>
    </div>
  );
}
